/**
 * CustomerAccount = the tenant (CONTEXT.md). Isolation boundary for all customer
 * data (ADR-0002). Lifecycle and identity shape per ADR-0003.
 */
export const ACCOUNT_LIFECYCLE_STATES = [
  'provisional',
  'qualifying',
  'qualified',
  'suspended',
] as const

export type AccountLifecycleState = (typeof ACCOUNT_LIFECYCLE_STATES)[number]

export function isAccountLifecycleState(value: unknown): value is AccountLifecycleState {
  return (
    typeof value === 'string' && (ACCOUNT_LIFECYCLE_STATES as readonly string[]).includes(value)
  )
}

/** Customer roles carried by a Membership (CONTEXT.md, ADR-0002 RBAC). */
export const CUSTOMER_ROLES = ['buyer', 'engineer', 'purchaser', 'account_admin'] as const

export type CustomerRole = (typeof CUSTOMER_ROLES)[number]

export function isCustomerRole(value: unknown): value is CustomerRole {
  return typeof value === 'string' && (CUSTOMER_ROLES as readonly string[]).includes(value)
}

/** Membership lifecycle (migration 0001 `membership_status`). */
export const MEMBERSHIP_STATUSES = ['invited', 'active', 'revoked'] as const

export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number]

export function isMembershipStatus(value: unknown): value is MembershipStatus {
  return typeof value === 'string' && (MEMBERSHIP_STATUSES as readonly string[]).includes(value)
}

/**
 * CustomerAccount projection shape (ADR-0002/0003).
 * - `accountCorrelationId` is Commerce-minted at creation — the durable anchor
 *   across the whole lifecycle (never the Salesforce id).
 * - `salesforceAccountId` is null until `qualified`; SF-owned inbound projection.
 * - `accountGroupId` is an inbound projection (SF-owned, never Commerce-written).
 */
export interface CustomerAccount {
  readonly id: string
  readonly accountCorrelationId: string
  readonly lifecycleState: AccountLifecycleState
  readonly salesforceAccountId: string | null
  readonly accountGroupId: string | null
}

/**
 * Membership = User x CustomerAccount x Role (ADR-0002). Modeled as a join from
 * day one so multi-account membership (deferred, RD-04) is a policy change, not
 * schema surgery. Shape matches migration 0001 `membership`.
 */
export interface Membership {
  readonly id: string
  readonly accountId: string
  readonly userId: string
  readonly role: CustomerRole
  readonly status: MembershipStatus
  readonly invitedByUserId: string | null
}
